<template>
  <div>
    <OfferSelectionComponent @action-btn="actionBtn" />
    <PaymentModal v-model:open="paymentConfirmationModalOpened" :title="$t('ETAB.PAGES.SUBSCRIPTION.@PAYMENT_CONFIRMATION_TITLE')">
      <PaymentConfirmationContent
        :selected-offer="selectedOffer"
        :formatted-date-now="getFormattedDateNow"
        @payment-subscription-action="paymentSubscriptionAction"
      />
    </PaymentModal>
    <PaymentModal v-model:open="paymentChoiceModalOpened" :title="$t('ETAB.PAGES.SUBSCRIPTION.@PAYMENT_CHOICE_TITLE')">
      <PaymentChoiceContent
        :selected-offer="selectedOffer"
        :payment-sessions="paymentSessions"
        :formatted-date-now="getFormattedDateNow"
        @select-payment-choice="selectPaymentChoice"
      />
    </PaymentModal>
    <PaymentSuccessModal
      v-model:open="paymentSuccessModalOpened"
      :btn-label="demandId ? $t('ETAB.PAGES.SUBSCRIPTION.LABELS.@PUBLISH_DEMAND') : $t('ETAB.PAGES.SUBSCRIPTION.LABELS.@CREATE_DEMAND')"
      @create-demand-or-success="createDemandOrSuccess"
      @on-close="onPaymentSuccessModalClose"
    />
  </div>
</template>

<script>
  import {
    ACTION_SUBSCRIPTION_GET_PAYMENT_SESSION,
    ACTION_SUBSCRIPTION_GET_SUBSCRIPTION_OFFERS,
    GETTER_SUBSCRIPTION_GET_PAYMENT_SESSION,
    GETTER_SUBSCRIPTION_GET_SELECTED_OFFER,
    ACTION_SUBSCRIPTION_GET_PAYMENT_LINK,
    GETTER_SUBSCRIPTION_GET_PAYMENT_LINK,
    ACTION_PROFILE_GET_SUBSCRIPTION_STATUS,
    GETTER_PROFILE_GET,
  } from "@etab/stores/types";
  import { useSubscriptionStore } from "@etab/stores/subscription";
  import { useProfileStore } from "@etab/stores/profile";
  import { mapStores } from "pinia";
  import { ETAB_ROUTES } from "@etab/config";
  import { EtabSubscriptionService, StripeService } from "@core/services";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { DatesHelpers, JavascriptHelpers } from "@core/helpers";
  import { Logger } from "@core/helpers/logger.helpers";
  import { PAYMENT_SESSION } from "@core/constants";
  import { MobileApp } from "@core/plugins/mobile/capacitor/app.plugin";
  import { isMobile } from "@core/plugins/mobile";

  import OfferSelectionComponent from "@etab/components/subscription/OfferSelection.component.vue";
  import PaymentModal from "@etab/components/subscription/Payment.modal.vue";
  import PaymentChoiceContent from "@etab/components/subscription/PaymentChoiceContent.component.vue";
  import PaymentConfirmationContent from "@etab/components/subscription/PaymentConfimationContent.component.vue";
  import PaymentSuccessModal from "@etab/components/subscription/PaymentSuccess.modal.vue";
  let listener;

  export default {
    components: {
      OfferSelectionComponent,
      PaymentModal,
      PaymentChoiceContent,
      PaymentConfirmationContent,
      PaymentSuccessModal,
    },
    props: {
      demandId: {
        type: String,
        default: undefined,
      },
    },
    emits: ["giveFeedback", "cancelOrGoBack"],
    data() {
      return {
        paymentChoiceModalOpened: false,
        paymentConfirmationModalOpened: false,
        paymentSuccessModalOpened: false,
      };
    },
    computed: {
      ...mapStores(useProfileStore, useSubscriptionStore),
      selectedOffer() {
        return this.subscriptionStore[GETTER_SUBSCRIPTION_GET_SELECTED_OFFER];
      },
      paymentSessions() {
        return this.subscriptionStore[GETTER_SUBSCRIPTION_GET_PAYMENT_SESSION];
      },
      paymentLink() {
        return this.subscriptionStore[GETTER_SUBSCRIPTION_GET_PAYMENT_LINK];
      },
      getFormattedDateNow() {
        return DatesHelpers.getDayjs().format("DD/MM/YYYY");
      },
      etabProfile() {
        return this.profileStore[GETTER_PROFILE_GET];
      },
    },
    created() {
      this.initSubscriptionOfferInformations();
    },
    beforeUnmount() {
      if (listener && typeof listener.remove === "function") {
        listener.remove();
      }
    },
    methods: {
      async initSubscriptionOfferInformations() {
        try {
          this.$_startGlobalLoading();
          const demandId = this.demandId;
          await this.subscriptionStore[ACTION_SUBSCRIPTION_GET_SUBSCRIPTION_OFFERS](demandId);
        } catch (error) {
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async actionBtn(btn) {
        try {
          this.$_startGlobalLoading();
          const offerId = this.selectedOffer.getId();
          if (btn.isOkBtn()) {
            // if user click on "subscribe" btn we init payment session data
            await this.subscriptionStore[ACTION_SUBSCRIPTION_GET_PAYMENT_SESSION](offerId);
            const activatedPayment = [PAYMENT_SESSION.STRIPE_ACTIVATED, PAYMENT_SESSION.GCL_ACTIVATED];
            // we check if there is already a GCL activated
            const hasAlreadyGCLActivated = this.paymentSessions.find((session) => activatedPayment.includes(session.getCode()));
            if (!hasAlreadyGCLActivated) {
              // if there not we open payment choice modal so he can choose between credit card and gcl
              this.openPaymentChoiceModal();
            } else {
              // if there is a gcl activated we redirect him to payment modal
              this.openPaymentConfirmationModal();
            }
          } else {
            this.$emit("cancelOrGoBack");
            this.$emit("giveFeedback", { isInterested: false, offerId, notifyUser: true });
          }
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          Logger.error(error);
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async paymentSubscriptionAction() {
        try {
          this.$_startGlobalLoading();
          const offerId = this.selectedOffer.getId();
          await EtabSubscriptionService.postSubscription(offerId);
          this.closePaymentConfirmationModal();
          this.openPaymentSuccessModal();
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          Logger.error(error);
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async selectPaymentChoice(paymentChoice) {
        try {
          this.$_startGlobalLoading();
          const offerId = this.selectedOffer.getId();
          const code = paymentChoice;
          await this.subscriptionStore[ACTION_SUBSCRIPTION_GET_PAYMENT_LINK]({ offerId, code });
          // stripe
          if (paymentChoice === PAYMENT_SESSION.CB_SESSION || paymentChoice === PAYMENT_SESSION.STRIPE_DEBIT_SESSION) {
            StripeService.stripeRedirect(this.paymentLink);
            /**
             * Stripe redirects the user outside of the app (on the Stripe protected payment page)
             * we want to check when the user comes back to the app (thus making it active)
             * when the app is active again we redirect the user to the dashboard
             */
            if (isMobile) {
              listener = MobileApp.plugin.addListener("appStateChange", (state) => {
                if (state.isActive) {
                  this.$router.push({ name: ETAB_ROUTES.dashboard.root.name }).catch((suppressedError) => {});
                }
              });
            }
            // GO CARDLESS
          } else if (paymentChoice === PAYMENT_SESSION.DIRECT_DEBIT_SESSION) {
            JavascriptHelpers.openUrl(this.paymentLink, "_blank");
            this.$emit("giveFeedback", { offerId, isInterested: true, notifyUser: true });
            this.closePaymentChoiceModal();
            if (this.demandId) {
              // if we're in creation demand process we redirect to success else we redirect to dashboard
              this.$router.push({ name: ETAB_ROUTES.creationSuccess.name }).catch((suppressedError) => {});
            } else {
              this.$router.push({ name: ETAB_ROUTES.dashboard.root.name }).catch((suppressedError) => {});
            }
          }
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          Logger.error(error);
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      openPaymentChoiceModal() {
        this.paymentChoiceModalOpened = true;
      },
      closePaymentChoiceModal() {
        this.paymentChoiceModalOpened = false;
      },
      openPaymentConfirmationModal() {
        this.paymentConfirmationModalOpened = true;
      },
      closePaymentConfirmationModal() {
        this.paymentConfirmationModalOpened = false;
      },
      openPaymentSuccessModal() {
        this.paymentSuccessModalOpened = true;
      },
      onPaymentSuccessModalClose() {
        // when success modal closes we check the subscription status to change the subscription component
        this.profileStore[ACTION_PROFILE_GET_SUBSCRIPTION_STATUS]();
      },
      createDemandOrSuccess() {
        if (this.demandId) {
          this.$router.replace({ name: ETAB_ROUTES.creationSuccess.name }).catch((suppressedError) => {});
        } else {
          this.$router.replace({ name: ETAB_ROUTES.creation.root.name }).catch((suppressedError) => {});
        }
      },
    },
  };
</script>
